/**
 * AIChatPanel - Slide-out panel for AI chat
 * Fixed to the right side of the viewport with smooth slide-in animation.
 * Uses the useAIChat hook for state management and SSE streaming.
 */
import React, { useEffect, useRef, useState } from 'react';
import { X, Sparkles, Trash2, Maximize2, Minimize2 } from 'lucide-react';
import { useAIChat } from '../../../../../packages/shared/src/hooks/useAIChat';
import { getApiBaseUrl } from '../../utils/config';
import AIChatMessage from './AIChatMessage';
import AIChatInput from './AIChatInput';

interface AIChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const PANEL_WIDTH = 420;

const AIChatPanel: React.FC<AIChatPanelProps> = ({ isOpen, onClose }) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Expand state. When expanded, the panel takes most of the viewport
  // instead of the 420px slide-out — useful when the assistant emits
  // wide tables (e.g. analyze_invoice_rules findings, the McDaniels
  // setup sheet). Collapsed state is the default to preserve the
  // existing minimal-UI feel.
  const [isExpanded, setIsExpanded] = useState(false);

  const { messages, isStreaming, error, sendMessage, clearMessages, cancelStream } = useAIChat({
    baseUrl: getApiBaseUrl(),
    getAuthHeaders: () => ({}), // Web uses cookies
    includeCredentials: true,
  });

  // Auto-scroll to bottom when new messages arrive or content updates
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  return (
    <>
      {/* Backdrop overlay */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          zIndex: 9999,
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
          transition: 'opacity 300ms ease',
        }}
      />

      {/* Panel — slides in from the right at PANEL_WIDTH by default.
          When isExpanded, switches to a centered "fullscreen" layout:
          fixed insets ~20px on each side, max width 1100px. Transition
          uses the same 300ms timing as the slide-in so the toggle feels
          consistent. transform is dropped in expanded mode so the
          inset/width transition reads correctly. */}
      <div
        style={{
          position: 'fixed',
          top: isExpanded ? 20 : 0,
          right: isExpanded ? 20 : 0,
          bottom: isExpanded ? 20 : 0,
          left: isExpanded ? 'max(20px, calc(50vw - 550px))' : 'auto',
          width: isExpanded ? 'auto' : `${PANEL_WIDTH}px`,
          maxWidth: isExpanded ? 'calc(100vw - 40px)' : '100vw',
          backgroundColor: 'var(--tc-background-secondary, #0B0B0B)',
          border: isExpanded ? '1px solid var(--tc-border-light, #333333)' : 'none',
          borderLeft: isExpanded ? '1px solid var(--tc-border-light, #333333)' : '1px solid var(--tc-border-light, #333333)',
          borderRadius: isExpanded ? 12 : 0,
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          transform: isExpanded ? 'none' : isOpen ? 'translateX(0)' : `translateX(${PANEL_WIDTH}px)`,
          transition:
            'transform 300ms ease, top 200ms ease, right 200ms ease, bottom 200ms ease, left 200ms ease, width 200ms ease, max-width 200ms ease, border-radius 200ms ease',
          boxShadow: isOpen ? '-8px 0 24px rgba(0, 0, 0, 0.4)' : 'none',
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px',
            borderBottom: '1px solid var(--tc-border-light, #333333)',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Sparkles size={18} style={{ color: 'var(--tc-text-selected, #6BA6F3)' }} />
            <span
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--tc-text-placeholder, #EEEEEE)',
                letterSpacing: '-0.01em',
              }}
            >
              AI Assistant
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <button
              onClick={() => setIsExpanded((v) => !v)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '6px',
                color: 'var(--tc-text-tertiary, #999999)',
                cursor: 'pointer',
                transition: 'background-color 0.2s, color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--tc-overlay-wash, rgba(255, 255, 255, 0.04))';
                e.currentTarget.style.color = '#ccc';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#888';
              }}
              aria-label={isExpanded ? 'Collapse panel' : 'Expand panel'}
              title={isExpanded ? 'Collapse panel' : 'Expand to fill viewport'}
            >
              {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
            {messages.length > 0 && (
              <button
                onClick={clearMessages}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'var(--tc-text-tertiary, #999999)',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s, color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--tc-overlay-wash, rgba(255, 255, 255, 0.04))';
                  e.currentTarget.style.color = '#999';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#666';
                }}
                aria-label="Clear chat"
                title="Clear chat"
              >
                <Trash2 size={15} />
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '6px',
                color: 'var(--tc-text-tertiary, #999999)',
                cursor: 'pointer',
                transition: 'background-color 0.2s, color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--tc-overlay-wash, rgba(255, 255, 255, 0.04))';
                e.currentTarget.style.color = '#ccc';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#888';
              }}
              aria-label="Close chat"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Messages area */}
        <div
          ref={messagesContainerRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingTop: '16px',
          }}
        >
          {messages.length === 0 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                padding: '32px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '16px',
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px',
                }}
              >
                <Sparkles size={24} style={{ color: 'var(--tc-text-selected, #6BA6F3)' }} />
              </div>
              <div
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--tc-text-placeholder, #EEEEEE)',
                  marginBottom: '8px',
                }}
              >
                How can I help?
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: 'var(--tc-text-tertiary, #999999)',
                  lineHeight: '1.5',
                  maxWidth: '280px',
                }}
              >
                Ask questions about your work orders, revenue, customers, vehicles, and more.
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  marginTop: '24px',
                  width: '100%',
                  maxWidth: '300px',
                }}
              >
                {['How many work orders were completed today?', 'What is our revenue this month?', 'Show me overdue work orders'].map(
                  (suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => sendMessage(suggestion)}
                      style={{
                        padding: '10px 14px',
                        backgroundColor: 'var(--tc-background-tertiary, #1A1A1A)',
                        border: '1px solid var(--tc-border-default, #2A2A1F)',
                        borderRadius: '10px',
                        color: 'var(--tc-text-tertiary, #999999)',
                        fontSize: '12px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'border-color 0.2s, color 0.2s',
                        lineHeight: '1.4',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#444';
                        e.currentTarget.style.color = '#ccc';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#2a2a2a';
                        e.currentTarget.style.color = '#999';
                      }}
                    >
                      {suggestion}
                    </button>
                  )
                )}
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg: { id: string; role: string; content: string; timestamp: Date; isStreaming?: boolean }) => (
                <AIChatMessage key={msg.id} message={msg as any} />
              ))}
            </>
          )}

          {/* Error message */}
          {error && (
            <div
              style={{
                margin: '8px 16px',
                padding: '10px 14px',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                borderRadius: '8px',
                color: 'var(--tc-brand-primary, #DA562C)',
                fontSize: '12px',
              }}
            >
              {error}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <AIChatInput onSend={sendMessage} onCancel={cancelStream} isStreaming={isStreaming} />
      </div>
    </>
  );
};

export default AIChatPanel;
